import React, { Component, createRef } from 'react'
import { Box, BoxProps, IconButton, styled, Tooltip, Typography } from '@mui/material'
import TextEditor, { textEditorClasses } from '@lib/Component/TextEditor'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { IFormInputBase } from '../types'
import { getErrorMessage } from '../helper'

interface IParams {
  id: string
  title: string
}

function CreateTextEditor<T>(params: IParams) {
  interface IProps extends IFormInputBase<T> {}

  interface IState {
    isFullscreen: boolean
  }

  class Editor extends Component<IProps, IState> {
    contentRef
    constructor(props: IProps) {
      super(props)
      this.state = {
        isFullscreen: false
      }
      this.contentRef = createRef<HTMLDivElement | null>()
    }

    componentDidMount() {
      document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    }

    componentWillUnmount() {
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    }

    handleFullscreenChange = () => {
      this.setState({ isFullscreen: !!document.fullscreenElement })
    }

    handleFullscreenToggle = () => {
      if (this.contentRef.current) {
        if (!document.fullscreenElement) {
          this.contentRef.current.requestFullscreen().catch((err) => {
            console.error(`Error attempting to enable full-screen mode: ${err.message}`)
          })
        } else {
          document.exitFullscreen()
        }
      }
    }

    getWrapClasses = () => {
      const classes = []
      if (this.state.isFullscreen) classes.push(textEditorClasses.fullscreen)
      return classes.join(' ')
    }

    render() {
      const errorMessage = getErrorMessage(this.props.messageErrors, this.props.name)
      const dValue = (this.props.name ? this.props.data?.[this.props.name] : '')?.toString() ?? ''

      return (
        <Box id={params.id} ref={this.contentRef} sx={{ backgroundColor: '#fff' }}>
          {this.state.isFullscreen && (
            <WrapTitle>
              <Typography variant='h4' sx={{ fontWeight: 600, flex: 1, textAlign: 'center' }}>
                {params.title}
              </Typography>
            </WrapTitle>
          )}
          <Wrap className={this.getWrapClasses()}>
            <WrapActions>
              <Tooltip title={this.state.isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} arrow>
                <CustomButton sx={{ color: '#06c' }} onClick={this.handleFullscreenToggle}>
                  {this.state.isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </CustomButton>
              </Tooltip>
            </WrapActions>
            <WrapTextEditor>
              <TextEditor
                defautValue={dValue}
                name={this.props.name?.toString() ?? ''}
                readOnly={this.props.disabled}
                error={errorMessage.error}
                onBlur={() => {
                  if (!this.props.name) return
                  this.props.onBlur?.(this.props.name)
                }}
              />
            </WrapTextEditor>
          </Wrap>
        </Box>
      )
    }
  }
  return Editor
}

export default CreateTextEditor

const editorHeight = 64

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: theme.breakpoints.values.xl,
  margin: '0 auto',
  position: 'relative',
  [`&.${textEditorClasses.fullscreen} .${textEditorClasses.root}`]: {
    height: `calc(100vh - ${editorHeight + 10}px)`
  },
  [`&.${textEditorClasses.fullscreen}`]: {
    width: '871px'
  }
}))

const WrapTitle = styled(Box)({
  height: `${editorHeight}px`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
})

const WrapActions = styled(({ children, ...props }: BoxProps) => (
  <Box {...props}>
    <Box>{children}</Box>
  </Box>
))({
  position: 'sticky',
  top: '-1px',
  zIndex: 2,
  '& > div': {
    height: 0,
    display: 'flex',
    justifyContent: 'right',
    padding: '0 6px'
  }
})

const CustomButton = styled(IconButton)({
  flex: '0 0 auto',
  marginTop: '1px',
  width: '40px',
  height: '40px'
})

const WrapTextEditor = styled(Box)({
  backgroundColor: '#fff',
  width: '100%',
  '& .ql-container': {
    fontFamily: '"Roboto", serif',
    fontSize: '14px'
  },
  '& .ql-toolbar': {
    position: 'sticky',
    top: '-1px',
    backgroundColor: '#fff',
    zIndex: 1
  }
})
